import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";
import { toast } from "sonner";

/**
 * Non-dismissible re-acceptance modal.
 *
 * Shown when the authenticated user's stored `terms_version` is missing or
 * older than the current `terms_version` advertised by the backend at
 * `/api/legal/info`. The user must tick the box and submit before they can
 * continue using the app — there is no escape hatch (no overlay click, no
 * Esc key, no close button).
 *
 * On accept, the backend updates both `terms_version` and
 * `terms_accepted_at` for the audit trail.
 */
export default function TermsReprompt() {
    const { user, updateUser } = useAuth();
    const [requiredVersion, setRequiredVersion] = useState(null);
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // Skip the check unless the user is logged in.
        if (!user) {
            setRequiredVersion(null);
            return;
        }
        let cancel = false;
        api.get("/legal/info")
            .then(({ data }) => { if (!cancel) setRequiredVersion(data?.terms_version || null); })
            .catch(() => { if (!cancel) setRequiredVersion(null); });
        return () => { cancel = true; };
    }, [user]);

    if (!user || !requiredVersion) return null;
    const stored = user.terms_version || "";
    if (stored === requiredVersion) return null;

    const onAccept = async () => {
        if (!accepted) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/accept-terms");
            updateUser(data.user);
            toast.success("Thanks — your account is up to date.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="terms-reprompt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="terms-reprompt-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
            {/* Heavy backdrop — non-dismissible (no onClick) */}
            <div className="absolute inset-0 bg-midnight-pine/85 backdrop-blur-sm" />

            <div className="relative w-full max-w-lg bg-snowcap rounded-lg shadow-2xl border border-ridge-line">
                <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-md bg-ember-orange/10 flex items-center justify-center shrink-0">
                            <ScrollText className="w-5 h-5 text-ember-orange" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-ember-orange">
                                Updated · {BRAND.legal.effectiveDate}
                            </div>
                            <h2
                                id="terms-reprompt-title"
                                className="font-display font-black text-xl text-midnight-pine leading-tight"
                            >
                                We&rsquo;ve updated our policies
                            </h2>
                        </div>
                    </div>

                    <p className="text-sm text-granite leading-relaxed mb-4">
                        To keep using {BRAND.name}, please review and re-accept the latest{" "}
                        <Link
                            to="/terms" target="_blank" rel="noopener"
                            data-testid="terms-reprompt-terms-link"
                            className="text-ember-orange font-semibold hover:underline"
                        >Terms of Service</Link>,{" "}
                        <Link
                            to="/privacy" target="_blank" rel="noopener"
                            data-testid="terms-reprompt-privacy-link"
                            className="text-ember-orange font-semibold hover:underline"
                        >Privacy Policy</Link>, and{" "}
                        <Link
                            to="/community-guidelines" target="_blank" rel="noopener"
                            data-testid="terms-reprompt-cg-link"
                            className="text-ember-orange font-semibold hover:underline"
                        >Community Guidelines</Link>.
                    </p>

                    <div className="bg-alpine-mist rounded-md p-3 mb-5 text-xs text-slate-gray space-y-1">
                        <div className="flex justify-between">
                            <span className="font-semibold">Your accepted version</span>
                            <span className="font-mono">{stored || "—"}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-semibold">Latest version</span>
                            <span className="font-mono text-midnight-pine font-bold">{requiredVersion}</span>
                        </div>
                    </div>

                    <label
                        htmlFor="terms-reprompt-accept"
                        data-testid="terms-reprompt-label"
                        className="flex items-start gap-3 mb-5 cursor-pointer select-none"
                    >
                        <input
                            id="terms-reprompt-accept"
                            data-testid="terms-reprompt-checkbox"
                            type="checkbox"
                            checked={accepted}
                            onChange={(e) => setAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-ridge-line text-ember-orange focus:ring-ember-orange/30 cursor-pointer"
                        />
                        <span className="text-sm text-granite leading-relaxed">
                            I have reviewed and agree to the latest Terms, Privacy Policy, and Community Guidelines (v{requiredVersion}).
                        </span>
                    </label>

                    <button
                        type="button"
                        data-testid="terms-reprompt-accept-btn"
                        onClick={onAccept}
                        disabled={!accepted || submitting}
                        className="w-full py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    >
                        {submitting ? "Saving…" : "Accept & continue"}
                    </button>

                    <p className="text-[11px] text-slate-gray text-center mt-4 leading-relaxed">
                        If you&rsquo;d rather not accept, you can{" "}
                        <Link
                            to="/settings/account"
                            data-testid="terms-reprompt-close-account"
                            className="underline hover:text-midnight-pine"
                        >close your account</Link>{" "}
                        from Settings.
                    </p>
                </div>
            </div>
        </div>
    );
}
